import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe, logout as logoutApi } from '../api/auth';
import { getAccounts, createAccount, updateAccount, updateHtsId, getDiscordWebhook, setDiscordWebhook, setStatsStartDate } from '../api/account';
import Sidebar from '../components/Sidebar';
import CollectingOverlay from '../components/CollectingOverlay';

function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // 계좌 관련
  const [accounts, setAccounts] = useState([]);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [accountForm, setAccountForm] = useState({
    account_no: '',
    app_key: '',
    app_secret: ''
  });
  const [accountSaving, setAccountSaving] = useState(false);
  
  // HTS ID 관련
  const [htsId, setHtsId] = useState('');
  const [htsSaving, setHtsSaving] = useState(false);
  
  // 디스코드 관련
  const [discordWebhook, setDiscordWebhookState] = useState('');
  const [discordSaving, setDiscordSaving] = useState(false);
  const [statsStartDate, setStatsStartDateState] = useState('');
  const [statsSaving, setStatsSaving] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const userData = await getMe();
        setUser(userData);
        
        const accountsData = await getAccounts();
        setAccounts(accountsData.accounts || []);

        if (accountsData.accounts?.length > 0) {
          setHtsId(accountsData.accounts[0].hts_id || '');
          // 통계 시작일 불러오기
          if (accountsData.accounts[0].stats_start_date) {
            setStatsStartDateState(accountsData.accounts[0].stats_start_date);
          }
        }

        const webhookData = await getDiscordWebhook();
        setDiscordWebhookState(webhookData.webhook_url || '');
        
      } catch (err) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  const handleLogout = () => {
    logoutApi();
    navigate('/login');
  };

  // 계좌 등록/수정
  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    setAccountSaving(true);
    try {
      if (isEditing && accounts.length > 0) {
        await updateAccount(accounts[0].id, accountForm);
        alert('계좌 정보가 수정되었습니다');
      } else {
        await createAccount(accountForm);
        alert('계좌가 등록되었습니다');
      }
      setShowAccountForm(false);
      setIsEditing(false);
      setAccountForm({ account_no: '', app_key: '', app_secret: '' });
      
      const accountsData = await getAccounts();
      setAccounts(accountsData.accounts || []);
    } catch (err) {
      alert((isEditing ? '수정' : '등록') + ' 실패: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAccountSaving(false);
    }
  };

  // 계좌 수정 모드
  const handleEditAccount = () => {
    if (accounts.length > 0) {
      setAccountForm({
        account_no: accounts[0].account_no,
        app_key: '',
        app_secret: ''
      });
      setIsEditing(true);
      setShowAccountForm(true);
    }
  };

  // HTS ID 저장
  const handleHtsSave = async () => {
    if (!accounts.length) {
      alert('먼저 계좌를 등록해주세요');
      return;
    }
    setHtsSaving(true);
    try {
      await updateHtsId(accounts[0].id, htsId);
      alert('HTS ID가 저장되었습니다');
    } catch (err) {
      alert('저장 실패: ' + (err.response?.data?.detail || err.message));
    } finally {
      setHtsSaving(false);
    }
  };

  // 디스코드 웹훅 저장
  const handleDiscordSave = async () => {
    setDiscordSaving(true);
    try {
      await setDiscordWebhook(discordWebhook);
      alert('디스코드 웹훅이 저장되었습니다');
    } catch (err) {
      alert('저장 실패: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDiscordSaving(false);
    }
  };

  const handleStatsDateSave = async () => {
    setStatsSaving(true);
    try {
      if (accounts.length > 0) {
        await setStatsStartDate(accounts[0].id, statsStartDate);
        alert("통계 시작일이 저장되었습니다");
      }
    } catch (err) {
      alert("저장 실패: " + (err.response?.data?.detail || err.message));
    } finally {
      setStatsSaving(false);
    }
  };

  if (loading) {
    return (
      <CollectingOverlay>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </CollectingOverlay>
    );
  }

  return (
  <CollectingOverlay>
    <div className="min-h-screen bg-gray-50">
      <Sidebar user={user} onLogout={handleLogout} />

      <main className="p-4 md:p-8 pt-20 md:pt-24 w-full max-w-6xl mx-auto">
        <header className="mb-3 md:mb-4">
          <h1 className="text-base md:text-2xl font-bold text-gray-800">설정</h1>
          <p className="text-xs text-gray-500">계좌 및 알림 설정을 관리합니다</p>
        </header>

        <div className="space-y-2 max-w-2xl">

          {/* 1. 계좌 등록 */}
          <div className="bg-white rounded-xl shadow-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm text-gray-500 font-medium">계좌 등록</h3>
                <p className="text-xs text-gray-400 mt-0.5">한국투자증권 API 계좌</p>
              </div>
              {accounts.length > 0 && !showAccountForm && (
                <button
                  onClick={handleEditAccount}
                  className="px-2 py-1 text-xs text-primary-600 hover:bg-primary-50 rounded-lg transition"
                >
                  수정
                </button>
              )}
            </div>

            {accounts.length > 0 && !showAccountForm ? (
              <div className="px-3 py-2 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{accounts[0].account_no}</p>
                    <p className="text-xs text-gray-500">
                      {accounts[0].is_connected ? '✓ 연결됨' : '연결 안됨'}
                    </p>
                  </div>
                  <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-600 rounded-full">
                    등록됨
                  </span>
                </div>
              </div>
            ) : (
              <>
                {!showAccountForm ? (
                  <button
                    onClick={() => setShowAccountForm(true)}
                    className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-primary-500 hover:text-primary-500 transition"
                  >
                    + 계좌 등록하기
                  </button>
                ) : (
                  <form onSubmit={handleAccountSubmit} className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">계좌번호</label>
                      <input
                        type="text"
                        value={accountForm.account_no}
                        onChange={(e) => setAccountForm({...accountForm, account_no: e.target.value})}
                        placeholder="12345678-01"
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">APP Key</label>
                      <input
                        type="text"
                        value={accountForm.app_key}
                        onChange={(e) => setAccountForm({...accountForm, app_key: e.target.value})}
                        placeholder={isEditing ? '변경하려면 새로 입력' : ''}
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required={!isEditing}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">APP Secret</label>
                      <input
                        type="password"
                        value={accountForm.app_secret}
                        onChange={(e) => setAccountForm({...accountForm, app_secret: e.target.value})}
                        placeholder={isEditing ? '변경하려면 새로 입력' : ''}
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required={!isEditing}
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAccountForm(false);
                          setIsEditing(false);
                          setAccountForm({ account_no: '', app_key: '', app_secret: '' });
                        }}
                        className="flex-1 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                      >
                        취소
                      </button>
                      <button
                        type="submit"
                        disabled={accountSaving}
                        className="flex-1 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition disabled:opacity-50"
                      >
                        {accountSaving ? '저장 중...' : (isEditing ? '수정' : '등록')}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>

          {/* 2. HTS ID 등록 */}
          <div className="bg-white rounded-xl shadow-sm p-3">
            <div className="mb-2">
              <h3 className="text-sm text-gray-500 font-medium">체결수신용 HTS ID</h3>
              <p className="text-xs text-gray-400 mt-0.5">실시간 체결 알림을 받기 위한 HTS ID</p>
            </div>

            <details className="mb-2">
              <summary className="text-xs text-primary-600 cursor-pointer hover:underline">
                HTS ID 확인하는 방법
              </summary>
              <div className="mt-2 p-2 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-1">
                <p>1. 한국투자증권 HTS (eFriend Plus) 실행</p>
                <p>2. 상단 메뉴 <strong>[도움말] → [정보]</strong> 클릭</p>
                <p>3. <strong>HTS ID</strong> 항목 확인 (예: user1234)</p>
              </div>
            </details>

            <div className="flex space-x-2">
              <input
                type="text"
                value={htsId}
                onChange={(e) => setHtsId(e.target.value)}
                placeholder="HTS ID 입력"
                className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={handleHtsSave}
                disabled={htsSaving || !accounts.length}
                className="px-4 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition disabled:opacity-50"
              >
                {htsSaving ? '저장 중...' : '저장'}
              </button>
            </div>
            {htsId && (
              <p className="mt-1.5 text-xs text-green-600">✓ HTS ID가 설정되어 있습니다</p>
            )}
          </div>

          {/* 3. 디스코드 웹훅 등록 */}
          <div className="bg-white rounded-xl shadow-sm p-3">
            <div className="mb-2">
              <h3 className="text-sm text-gray-500 font-medium">디스코드 알림</h3>
              <p className="text-xs text-gray-400 mt-0.5">매수/매도 체결 시 디스코드로 알림</p>
            </div>

            <details className="mb-2">
              <summary className="text-xs text-primary-600 cursor-pointer hover:underline">
                웹훅 URL 만드는 방법
              </summary>
              <div className="mt-2 p-2 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-1">
                <p>1. 디스코드에서 알림 받을 채널의 <strong>설정(⚙️)</strong> 클릭</p>
                <p>2. <strong>연동</strong> → <strong>웹후크</strong> 메뉴 선택</p>
                <p>3. <strong>새 웹후크</strong> 버튼 클릭</p>
                <p>4. 이름 설정 후 <strong>웹후크 URL 복사</strong></p>
                <p>5. 아래 입력창에 붙여넣기</p>
              </div>
            </details>

            <div className="flex space-x-2">
              <input
                type="text"
                value={discordWebhook}
                onChange={(e) => setDiscordWebhookState(e.target.value)}
                placeholder="https://discord.com/api/webhooks/..."
                className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={handleDiscordSave}
                disabled={discordSaving}
                className="px-4 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition disabled:opacity-50"
              >
                {discordSaving ? '저장 중...' : '저장'}
              </button>
            </div>
            {discordWebhook && (
              <p className="mt-1.5 text-xs text-green-600">✓ 디스코드 웹훅이 설정되어 있습니다</p>
            )}
          </div>

          {/* 4. 통계 시작일 설정 */}
          <div className="bg-white rounded-xl shadow-sm p-3">
            <div className="mb-2">
              <h3 className="text-sm text-gray-500 font-medium">통계 시작일</h3>
              <p className="text-xs text-gray-400 mt-0.5">이 날짜 이후의 거래만 통계에 포함</p>
            </div>
            <div className="flex space-x-2">
              <input
                type="date"
                value={statsStartDate}
                onChange={(e) => setStatsStartDateState(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={handleStatsDateSave}
                disabled={statsSaving}
                className="px-4 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition disabled:opacity-50"
              >
                {statsSaving ? '저장 중...' : '저장'}
              </button>
            </div>
            {statsStartDate && (
              <p className="mt-1.5 text-xs text-green-600">✓ {statsStartDate} 이후 거래만 통계에 반영</p>
            )}
          </div>

        </div>
      </main>
    </div>
  </CollectingOverlay>
  );
}

export default Settings;
